import { useEffect, useRef, useState } from "react";
import Player from "@vimeo/player";

/**
 * Plays a legacy Vimeo lesson. Renders the embed iframe (which already handles
 * autoplay and sizing) and attaches the Vimeo Player SDK to it purely for
 * events: `timeupdate` (position saving) and `ended` (auto-advance). Uploads go
 * to Mux now, so this only serves lessons created before that migration.
 */
export function VimeoLessonPlayer({
  videoUrl,
  title,
  startAt,
  onReady,
  onPosition,
  onEnded,
}: {
  videoUrl: string;
  title: string;
  startAt: number;
  onReady: () => void;
  onPosition: (seconds: number) => void;
  onEnded: () => void;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<Player | null>(null);
  // When the video ends, Vimeo shows its own end screen (share + copyable embed
  // + related videos). We cover it with our own overlay so it never appears.
  const [ended, setEnded] = useState(false);
  // Keep the latest callbacks so the SDK listeners (bound once per video) always
  // call current handlers without re-creating the player on every render.
  const handlers = useRef({ onReady, onPosition, onEnded, startAt });
  handlers.current = { onReady, onPosition, onEnded, startAt };
  const videoId = videoUrl.match(/vimeo\.com\/(?:video\/)?(\d+)/)?.[1] ?? "";

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const player = new Player(iframe);
    playerRef.current = player;
    setEnded(false);
    player
      .ready()
      .then(() => {
        handlers.current.onReady();
        if (handlers.current.startAt > 0)
          return player.setCurrentTime(handlers.current.startAt);
        return undefined;
      })
      .catch(() => undefined);
    // The embed autoplays with sound. When the browser blocks that (no user
    // activation yet, e.g. a lesson URL opened directly), it stays paused:
    // fall back to muted autoplay so the lesson still starts.
    const fallback = window.setTimeout(() => {
      player
        .getPaused()
        .then((paused) =>
          paused ? player.setMuted(true).then(() => player.play()) : undefined,
        )
        .catch(() => undefined);
    }, 2500);
    const handleTime = (data: { seconds: number }) =>
      handlers.current.onPosition(Math.floor(data.seconds));
    const handleEnd = () => {
      setEnded(true);
      handlers.current.onEnded();
    };
    player.on("timeupdate", handleTime);
    player.on("ended", handleEnd);
    return () => {
      window.clearTimeout(fallback);
      player.off("timeupdate", handleTime);
      player.off("ended", handleEnd);
      playerRef.current = null;
    };
  }, [videoId]);

  return (
    <>
      <iframe
        ref={iframeRef}
        title={title}
        src={`https://player.vimeo.com/video/${videoId}?autoplay=1&title=0&byline=0&portrait=0&dnt=1`}
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 h-full w-full"
      />
      {ended && (
        <div className="bg-ink absolute inset-0 z-10 grid place-items-center px-6 text-center">
          <div>
            <p className="text-paper/60 font-mono text-xs font-bold uppercase tracking-widest">
              Clase completada
            </p>
            <button
              type="button"
              onClick={() => {
                const player = playerRef.current;
                if (!player) return;
                setEnded(false);
                player
                  .setCurrentTime(0)
                  .then(() => player.play())
                  .catch(() => undefined);
              }}
              className="border-paper bg-acid text-ink font-display border-chip mt-4 px-5 py-2 text-sm font-bold"
            >
              Volver a ver
            </button>
          </div>
        </div>
      )}
    </>
  );
}
